import { GROUP_COLUMN_PREFIX, PIVOT_SEPARATOR } from 'common/visualizations/views/agGridReact/Constants';
import { type ColDef, type GridApi } from 'ag-grid-community';
import { type TableColumn, type ExtraOptions } from '../../types';
import { type TableColumnFormat, type ExportData } from 'common/visualizations/views/agGridReact/types';
import { type CustomColDefContext } from 'common/types/agGrid/context';
import { SoQLType } from 'common/types/soql';
import { isTablePrintingImprovementFormattingEnabled } from 'common/visualizations/helpers/VifSelectors';
import { getDefaultColumnAlignment } from 'common/visualizations/views/agGridReact/helpers/TableColumnAlignment';

const getEnhancedDisplayName = (
  colId: string,
  colDef: ColDef,
  columnFormats: { [key: string]: TableColumnFormat },
  exportData: ExportData,
): string => {
  if (!colId.includes(PIVOT_SEPARATOR)) {
    return colDef.headerName || colId;
  }

  // For pivot columns, get the base column name and use it for lookup
  const context = colDef.context as CustomColDefContext | undefined;
  const baseColumnName = context?.pivotValueColumn || colId;

  const columnFormat = columnFormats[baseColumnName];
  const exportColumn = exportData.columns.find((column) => column.fieldName === baseColumnName);
  return columnFormat?.displayName || exportColumn?.name || baseColumnName;
};

export const buildPivotTableColumns = (
  gridApi: GridApi,
  exportData: ExportData,
  extraOptions: ExtraOptions,
): TableColumn[] => {
  const allDisplayedColumns = gridApi.getAllDisplayedColumns();
  const nonGroupedColumns = allDisplayedColumns.filter((col) => {
    const colId = col.getColId();
    return !colId.startsWith(GROUP_COLUMN_PREFIX);
  });
  const groupedColumns = gridApi.getRowGroupColumns();
  const allColumns = [...groupedColumns, ...nonGroupedColumns];

  // Keep track of how many columns are row group columns for proper isGrouped marking
  const groupedColumnCount = groupedColumns.length;

  const pivotTableColumns = allColumns.map((col, index) => {
    const colDef = col.getColDef();
    const colId = col.getColId();

    const columnFormat: TableColumnFormat | undefined = extraOptions.columnFormats[colId];
    const columnType = findColumnDataType(colDef, exportData);

    // Create a TableColumn object to get the proper data-type-based alignment
    const tableColumn: TableColumn = { field: colId, type: columnType } as TableColumn;
    const textAlign = getDefaultColumnAlignment(tableColumn);
    const align = columnFormat?.format?.align;
    const isAlign = columnFormat?.isFormatValue ?? true;

    return {
      field: colId,
      displayName: isTablePrintingImprovementFormattingEnabled()
        ? getEnhancedDisplayName(colId, colDef, extraOptions.columnFormats, exportData)
        : colDef.headerName || colId,
      type: columnType,
      aggFunc: colDef?.aggFunc?.toString(),
      width: col.getActualWidth(),
      isGrouped: extraOptions.featureFlags?.isTablePrintingImprovementGroupingLayoutEnabled
        ? index < groupedColumnCount // Mark row group columns as grouped when indented layout enabled
        : false, // Keep existing behavior (all columns ungrouped) when feature flag off
      align: isAlign ? align || textAlign : textAlign,
    };
  });
  return pivotTableColumns;
};

export const findColumnDataType = (colDef: ColDef, exportData: ExportData): SoQLType => {
  let columnName = colDef.colId;
  if (columnName?.includes(PIVOT_SEPARATOR)) {
    // If this is a pivot column, use it's associated value column name
    columnName = (colDef.context as CustomColDefContext | undefined)?.pivotValueColumn;
  }
  const columnData = exportData.columns.find((col) => col.fieldName === columnName);
  return columnData?.dataTypeName ?? SoQLType.SoQLTextT;
};
